import { HttpClient, HttpContext, HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { DestroyRef, inject, Injectable, signal, WritableSignal } from "@angular/core";
import { Router } from "@angular/router";
import { JwtHelperService } from "@auth0/angular-jwt";
import { ActivateBraceletDto, LoginDto, LoginErrorCodeEnum, LoginErrorMessage, LoginErrorResponse, LoginResponse, LoginSuccess, Wallet } from "@core/models";
import { environment } from "@env/environment";
import { catchError, map, Observable, of, tap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IS_PUBLIC } from "@core/interceptors/auth.interceptor";
import { WalletService } from "./wallet.service";

@Injectable()
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly jwtHelper = inject(JwtHelperService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly CONTEXT = {context: new HttpContext().set(IS_PUBLIC, true)};
  private readonly TOKEN_EXPIRY_THRESHOLD_MINUTES = 5;
  private readonly walletService = inject(WalletService);

  loginError: WritableSignal<string | null> = signal(null);
  isAuthAttemptsExceeded: WritableSignal<boolean> = signal(false); //is maximum attempts of authentication exceeded
  authRetryDelayMinutes: WritableSignal<number> = signal(0); //remaining minutes until re-authentication is allowed

  get user(): WritableSignal<Wallet | null> {
    const token = localStorage.getItem('token');
    return signal(token ? this.jwtHelper.decodeToken(token) : null);
  }

  isAuthenticated(): boolean {
    return !this.jwtHelper.isTokenExpired();
  }

  login(body: LoginDto): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiURL}/auth/login`, body, this.CONTEXT)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          if (errorResponse.status === HttpStatusCode.Forbidden) { //403 Http response
            //if user has reached the maximum number of login attempts
            const maxAuthAttemptsErrorMessage = this.maxAuthAttemptsError(errorResponse.error);
            if (maxAuthAttemptsErrorMessage) {
              this.loginError.set(null);
              this.isAuthAttemptsExceeded.set(true);
              //auth attempts error message includes remaining minutes
              this.authRetryDelayMinutes.set(Number(maxAuthAttemptsErrorMessage.message));
            } else {
              this.resetErrorStates(); //reset all error states
              this.loginError.set('Invalid credentials');
            }
          }
          return of();
        }),
        tap(data => {
          const loginSuccessData = data as LoginSuccess;
          this.resetErrorStates(); //reset all error states
          this.walletService.setWallet(loginSuccessData.data.wallet);
          this.storeTokens(loginSuccessData);
          this.scheduleTokenRefresh(loginSuccessData.data.token);
          this.router.navigate(['/']);
        })
      );
  }

  logout(): void {
    // if you don't have any backend route to invalidate the refresh token
    // then just remove localStorage items and redirect to login route
    const refresh_token = localStorage.getItem('refresh_token');
    this.http.get<LoginResponse>(`${environment.apiURL}/auth/logout`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        this.router.navigate(['/auth/login']);
      });
  }

  refreshToken(): Observable<LoginResponse | null> {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      return of();
    }

    return this.http.get<LoginResponse>(
      `${environment.apiURL}/auth/refresh-token`)
      .pipe(
        catchError(() => of()),
        tap(data => {
          const loginSuccessData = data as LoginSuccess;
          this.storeTokens(loginSuccessData);
          this.scheduleTokenRefresh(loginSuccessData.data.token);
        })
      );
  }

  connectBracelet(dto: ActivateBraceletDto): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${environment.apiURL}/auth/activate`, dto, this.CONTEXT)
      .pipe(
        tap(data => {
          const loginSuccessData = data as LoginSuccess;
          this.storeTokens(loginSuccessData);
          this.scheduleTokenRefresh(loginSuccessData.data.token);
          this.router.navigate(['/']);
        })
      );
  }

  //check the status of bracelet (ready === device exist & is active)
  checkBracelet(deviceId: string): Observable<boolean> {
    return this.http.get<boolean>(`${environment.apiURL}/auth/ready?number=${deviceId}`, this.CONTEXT).pipe(
      map((response: any) => response.data.ready),
      catchError(err => {
        console.error('Error in checkBracelet', err);
        return of(false);
      })
    );
  }

  scheduleTokenRefresh(token: string): void {
    const expirationTime = this.jwtHelper.getTokenExpirationDate(token)?.getTime();
    const refreshTime = expirationTime ? expirationTime - this.TOKEN_EXPIRY_THRESHOLD_MINUTES * 60 * 1000 : Date.now();
    const refreshInterval = refreshTime - Date.now();

    if (refreshInterval > 0) {
      setTimeout(() => {
        this.refreshToken()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      }, refreshInterval);
    }
  }

  storeTokens(data: LoginSuccess): void {
    localStorage.setItem('token', data.data.token);
  }

  private resetErrorStates(): void {
    this.loginError.set(null);
    this.isAuthAttemptsExceeded.set(false);
    this.authRetryDelayMinutes.set(0);
  }

  private maxAuthAttemptsError(error: LoginErrorResponse): LoginErrorMessage | undefined {
    return error.messages.find(message => message.code === LoginErrorCodeEnum.AUTH_MAX_ATTEMPT);
  }
}
